import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// SVG icons with isActive prop
import { CoursesIcon, AssignmentsIcon, ExamsIcon, QuizIcon, StudyMaterialsIcon, TimeTableIcon } from './Icons';

/**
 * Tab item styled to match the provided example
 */
const TabItem = ({ text, icon, isActive, onClick, disabled }) => {
    const handleClick = (e) => {
        e.stopPropagation(); // Stop event propagation
        if (!disabled) {
            onClick();
        }
    };

    return (
        <div
            className={`self-stretch px-2 sm:px-4 py-2 ${isActive ? 'bg-blue-600' : 'bg-white'} rounded flex justify-start items-center gap-2 sm:gap-3 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            onClick={handleClick}
        >
            <div className="flex-shrink-0">
                {icon}
            </div>
            <div className={`justify-center ${isActive ? 'text-white' : 'text-neutral-900'} text-sm sm:text-base font-normal font-circular capitalize leading-normal truncate`}>
                {text}
                {disabled && <span className="text-xs ml-1">(soon)</span>}
            </div>
        </div>
    );
};

/**
 * ActiveTab - A navigation tab component with active and inactive states
 */
const ActiveTab = ({ tabs = [], defaultActiveTab = 0, onTabChange }) => {
    // State to track which tab is active
    const [activeTab, setActiveTab] = useState(defaultActiveTab);
    
    // State to track if we should use mobile view
    const [isMobileView, setIsMobileView] = useState(false);
    
    // Update view based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth < 768);
        };
        
        // Initial check
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Handle tab click
    const handleTabClick = (index) => {
        if (tabs[index].disabled) return;
        
        setActiveTab(index);
        if (onTabChange) {
            onTabChange(index);
        }
    };

    // Handle select change for mobile view
    const handleSelectChange = (e) => {
        e.stopPropagation(); // Stop event propagation
        const index = Number(e.target.value);
        if (!tabs[index].disabled) {
            handleTabClick(index);
        }
    };

    // Mobile view - render as a dropdown/select
    if (isMobileView) {
        return (
            <div className="w-full px-4 py-3 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.05)] border-t border-neutral-200">
                <select 
                    className="w-full p-2 border border-gray-300 rounded"
                    value={activeTab}
                    onChange={handleSelectChange}
                    onClick={(e) => e.stopPropagation()} // Stop event propagation
                >
                    {tabs.map((tab, index) => (
                        <option key={index} value={index} disabled={tab.disabled}>
                            {tab.text}{tab.disabled ? " (soon)" : ""}
                        </option>
                    ))}
                </select>
            </div>
        );
    }

    // Desktop view - render as horizontal tabs
    return (
        <div className="w-full px-3 md:px-6 py-2 md:py-2.5 bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.05)] border-t border-neutral-200 inline-flex justify-start items-center gap-2 md:gap-6 overflow-x-auto">
            {tabs.map((tab, index) => (
                <TabItem
                    key={index}
                    text={tab.text}
                    icon={React.cloneElement(tab.icon, { isActive: activeTab === index })}
                    isActive={activeTab === index}
                    onClick={() => handleTabClick(index)}
                    disabled={tab.disabled}
                />
            ))}
        </div>
    );
};

/**
 * Navigation - Main navigation component that uses ActiveTab
 */
const LearningNavigation = ({ onTabChange }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Define your tabs with text and icons
    const tabs = [
        { text: "Courses", icon: <CoursesIcon />, path: "courses", disabled: false },
        { text: "Assignments", icon: <AssignmentsIcon />, path: "assignments", disabled: true },
        { text: "Exams", icon: <ExamsIcon />, path: "exams", disabled: true },
        { text: "Quiz", icon: <QuizIcon />, path: "quiz", disabled: true },
        { text: "Study Materials", icon: <StudyMaterialsIcon />, path: "materials", disabled: false },
        { text: "Time Table", icon: <TimeTableIcon />, path: "timetable", disabled: true }
    ];

    // Determine active tab from URL
    const getActiveTabFromURL = () => {
        const path = location.pathname;

        // Extract the last segment of the path
        const pathSegment = path.split('/').pop();

        // Find the index of the tab with matching path
        const activeIndex = tabs.findIndex(tab => tab.path === pathSegment);

        // Return the found index, or 0 if not found
        return activeIndex >= 0 ? activeIndex : '';
    };

    // Get default tab index based on URL
    const defaultTabIndex = getActiveTabFromURL();

    // Handle tab change
    const handleTabChange = (index) => {
        if (tabs[index].disabled) return;
        
        console.log(`Switched to tab: ${tabs[index].text}, path: ${tabs[index].path}`);

        // Use the provided callback if available
        if (onTabChange) {
            onTabChange(index);
        } else {
            // Direct navigation
            navigate(`/learning/${tabs[index].path}`);
        }
    };

    return (
        <ActiveTab
            tabs={tabs}
            defaultActiveTab={defaultTabIndex}
            onTabChange={handleTabChange}
        />
    );
};

export default LearningNavigation;